//@flow
import * as Immutable from 'immutable';

import StringUtils from 'common/utils/StringUtils';
import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';

import I18nHelper from 'common/helpers/I18nHelper';

import { MasterOutlet, Holiday } from 'common/models/flow/MasterOutlet';
import type {
    MasterOutletModelType,
    HolidayModelType
} from 'common/models/flow/MasterOutlet';

import { MultiDaySlot } from './MultiDaySlot';
import { LocationInfo } from './LocationInfo';
import { HistoricalData } from './HistoricalData';
import { SalesCategory } from './SalesCategory';
import { SalesCategoryId } from './SalesCategoryId';
import { TimeSlot } from './TimeSlot';
import { AuditMetadata } from './AuditMetadata';

//Validation stuff
import {
    validatable,
    validate,
    validateAll,
    validateId,
    validateZoneId,
    softValidateAll
} from 'common/utils/ValidationUtil';
import RegExUtil from 'common/utils/RegExUtil';

import { ChannelData } from './ChannelData';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';

import { TeamId } from './TeamId';

import { VisitFrequencyInfo } from './VisitFrequencyInfo';

import { DayOfWeekType } from 'common/enums/DateTimeEnums';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';

const translationsNamespace = ['validations'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

export const OutletLocationTypeEnum = new EntityTypeEnum({
    CLIENT: 'CLIENT',
    WAREHOUSE: 'WAREHOUSE',
    SUPPLIER: 'SUPPLIER'
});

const validateNumber = (value: any) => {
    if (!DataUtils.isEmpty(value) && !NumberUtils.isNumeric(value)) {
        return nsTranslate('outletValidationErrors.volumeNotANumber');
    }
};

@validatable
class InternalMasterOutlet extends MasterOutlet {
    static fromJS(json: MasterOutletModelType): InternalMasterOutlet {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.channels = Immutable.List(state.channels).map(item =>
            ChannelData.fromJS(item)
        );
        state.holidays = state.holidays
            ? Immutable.List(state.holidays).map(item =>
                  InternalHoliday.fromJS(item)
              )
            : state.holidays;
        state.salesCategory = state.salesCategory
            ? SalesCategory.fromJS(state.salesCategory)
            : state.salesCategory;
        state.salesCategoryId = state.salesCategoryId
            ? SalesCategoryId.fromJS(state.salesCategoryId)
            : state.salesCategoryId;
        state.timeSlots = Immutable.List(state.timeSlots).map(item =>
            MultiDaySlot.fromJS(item)
        );
        state.locationInfo = LocationInfo.fromJS(state.locationInfo);
        state.historicalData = state.historicalData
            ? HistoricalData.fromJS(state.historicalData)
            : state.historicalData;
        state.visitFrequencyInfo = VisitFrequencyInfo.fromJS(
            state.visitFrequencyInfo
        );
        state.mandatoryDays = state.mandatoryDays
            ? Immutable.List(state.mandatoryDays)
            : state.mandatoryDays;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get salesCategory(): ?SalesCategory {
        return this._state.get('salesCategory');
    }

    get timeSlots(): Immutable.List<MultiDaySlot> {
        return this._state.get('timeSlots');
    }

    get locationInfo(): LocationInfo {
        return this._state.get('locationInfo');
    }

    get historicalData(): ?HistoricalData {
        return this._state.get('historicalData');
    }

    get channels(): Immutable.List<ChannelData> {
        return this._state.get('channels');
    }

    get categories(): Immutable.List<CategoriesData> {
        return this._state.get('categories');
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { historicalData, locationInfo, visitFrequencyInfo } = this;
        if (historicalData && historicalData.hasValidationErrors()) {
            return true;
        }
        if (locationInfo && locationInfo.hasValidationErrors()) {
            return true;
        }
        if (visitFrequencyInfo && visitFrequencyInfo.hasValidationErrors()) {
            return true;
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let {
            locationInfo,
            timeSlots,
            historicalData,
            visitFrequencyInfo
        } = this;
        if (locationInfo) {
            locationInfo = locationInfo.setAllValidationErrors();
        }
        if (timeSlots) {
            timeSlots = timeSlots.map(timeSlot =>
                timeSlot.setAllValidationErrors()
            );
        }
        if (historicalData) {
            historicalData = historicalData.setAllValidationErrors();
        }

        if (visitFrequencyInfo) {
            visitFrequencyInfo = visitFrequencyInfo.setAllValidationErrors();
        }

        return this.setLocationInfo(locationInfo)
            .setTimeSlots(timeSlots)
            .setHistoricalData(historicalData)
            .setVisitFrequencyInfo(visitFrequencyInfo);
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('outletValidationErrors.codeEmpty');
        }
        const trimmedCode = code.trim();
        if (trimmedCode.length < 2) {
            return nsTranslate('outletValidationErrors.codeLessThan2');
        }
        if (trimmedCode.length > 32) {
            return nsTranslate('outletValidationErrors.codeGreaterThan32');
        }
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            StringUtils.isEmpty(this.name) ||
            StringUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('outletValidationErrors.nameEmpty');
        }
    }

    @validate
    setTeams(teams: Immutable.List<TeamId>): this {
        return super.setTeams(teams);
    }

    validateTeams() {
        if (DataUtils.isEmpty(this.teams) || this.teams.size <= 0) {
            return nsTranslate('outletValidationErrors.teamEmpty');
        }
    }

    @validate
    setTransactionDuration(transactionDuration: number): this {
        return super.setTransactionDuration(transactionDuration);
    }

    validateTransactionDuration() {
        if (
            !DataUtils.isEmpty(this.transactionDuration) &&
            !NumberUtils.isWholeNumber(this.transactionDuration)
        ) {
            return nsTranslate(
                'outletValidationErrors.transactionDurationNotAWholeNumber'
            );
        }
    }

    @validate
    setSalesCategoryId(salesCategory: ?SalesCategoryId): this {
        return super.setSalesCategory(salesCategory);
    }

    validateSalesCategoryId() {
        if (DataUtils.isEmpty(this.salesCategoryId?.salesCategoryId)) {
            return nsTranslate('outletValidationErrors.salesCategoryEmpty');
        }
    }

    @validate
    setTimeSlots(timeSlots: Immutable.List<MultiDaySlot>): this {
        return super.setTimeSlots(timeSlots);
    }

    validateTimeSlots() {
        if (
            DataUtils.isEmpty(this.timeSlots) ||
            this.timeSlots.size <= 0 ||
            this.timeSlots.every(
                timeSlot => !timeSlot.slots || timeSlot.slots.size === 0
            )
        ) {
            return nsTranslate('outletValidationErrors.timeSlotsEmpty');
        }
    }

    @validate
    setZoneId(zoneId: string): this {
        return super.setZoneId(zoneId);
    }

    validateZoneId() {
        const { zoneId } = this;
        if (zoneId) {
            return validateZoneId(zoneId);
        }
    }

    setChannel(channel: ChannelData) {
        let channels = this._state.get('channels');
        if (channels) {
            const index = channels.findIndex(
                channelItem => channelItem.id === channel.id
            );
            if (index > -1) {
                if (channel.name === null) {
                    channels = channels.delete(index);
                } else {
                    channels = channels.update(index, channelItem =>
                        channelItem.set('name', channel.name)
                    );
                }
            } else {
                if (channel.name) {
                    channels = channels.push(ChannelData.fromJS(channel));
                }
            }
            return super.setChannels(channels);
        } else {
            return super.setChannels(
                Immutable.List(
                    channel.name ? [ChannelData.fromJS(channel)] : []
                )
            );
        }
    }

    setCategory(category: CategoriesData) {
        let categories = this._state.get('categories');
        if (categories) {
            const index = categories.findIndex(
                categoryItem => categoryItem.id === category.id
            );
            if (index > -1) {
                if (category.name == null) {
                    categories = categories.delete(index);
                } else {
                    categories = categories.update(index, categoryItem =>
                        categoryItem.set('name', category.name)
                    );
                }
            } else {
                if (category.name) {
                    categories = categories.push(
                        CategoriesData.fromJS(category)
                    );
                }
            }
            return super.setCategories(categories);
        } else {
            return super.setCategories(
                Immutable.List(
                    category.name ? [CategoriesData.fromJS(category)] : []
                )
            );
        }
    }

    sortTimeSlots(): this {
        return this.setTimeSlots(TimeSlot.sortTimeSlots(this.timeSlots));
    }

    initializeWeekSlots(): this {
        return this.setTimeSlots(
            TimeSlot.initializeAllTimeSlots(this.timeSlots)
        );
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    @validate
    setAverageVolume(averageVolume: number): this {
        return super.setAverageVolume(averageVolume);
    }

    validateAverageVolume(): ?string {
        return validateNumber(this.averageVolume);
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedOutlet = super.defaultSanitize();
        if (sanitizedOutlet) {
            const sanitizedLocationInfo = sanitizedOutlet.locationInfo?.defaultSanitize();
            const sanitizedHistoricalData = sanitizedOutlet.historicalData?.defaultSanitize();
            let { timeSlots: sanitizedTimeSlots } = sanitizedOutlet;
            if (sanitizedTimeSlots) {
                sanitizedTimeSlots = sanitizedTimeSlots.filter(
                    timeSlot => timeSlot.slots.size
                );
            }
            return sanitizedOutlet
                .setLocationInfo(sanitizedLocationInfo)
                .setHistoricalData(sanitizedHistoricalData)
                .setTimeSlots(sanitizedTimeSlots);
        }
        return sanitizedOutlet;
    }

    static defaultInit({ props }): InternalMasterOutlet {
        const locationInfo = LocationInfo.defaultInit({ props });
        const historicalData = HistoricalData.defaultInit({ props });

        return InternalMasterOutlet.fromJS({ transactionDuration: 0 })
            .setLocationInfo(locationInfo)
            .setHistoricalData(historicalData);
    }

    get visitFrequencyInfo(): VisitFrequencyInfo {
        return this._state.get('visitFrequencyInfo');
    }

    get mandatoryDays(): ?Immutable.List<DayOfWeekType> {
        return this._state.get('mandatoryDays');
    }

    get salesCategoryId(): ?SalesCategoryId {
        return this._state.get('salesCategoryId');
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get holidays(): ?Immutable.List<Holiday> {
        return this._state.get('holidays');
    }
}

class InternalHoliday extends Holiday {
    static fromJS(json: HolidayModelType): InternalHoliday {
        const state: Object = Object.assign({}, json);
        state.days = Immutable.List(state.days);
        return new this(Immutable.Map(state));
    }
}

export { InternalMasterOutlet as MasterOutlet, InternalHoliday as Holiday };
